import { AfterContentInit, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { concat, EMPTY, from, interval, Observable, of, Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, takeUntil, takeWhile, tap, toArray } from 'rxjs/operators';
import * as Bowser from 'bowser';

import { environment } from '@env/environment';
import { Logger, untilDestroyed } from '@app/core';
import {
  SecurityVerificationType,
  LivenessStatusEnum,
  SecurityVerificationEnum,
  SecurityVerificationFileTypeEnum,
  SecurityVerificationPayloadType,
  BiometriqueSignedUrls,
  UserBiometricValidation,
  UserBiometricValidationStatusEnum,
  VerificationImages
} from '@app/models';
import { AppService, AuthenticationService, DocumentService, ErrorHandlerService, NotyService, VerificationService, WhitelabelService } from '@app/services';
import { LoaderService } from '@app/shared/loader/loader.service';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

export type ModalResult = SecurityVerificationType | UserBiometricValidation;
export interface ModalPublicProperties {
  verification: SecurityVerificationType;
  documentId?: string;
  operationId?: string;
}

const Steps = {
  Intro: 'Intro',
  HasBackPrompt: 'HasBackPrompt',
  MobilePrompt: 'MobilePrompt',
  CameraError: 'CameraError',
  VerificationError: 'VerificationError',
  BiometricAndTextExtraction: 'BiometricAndTextExtraction',
  BiometricAndTextExtractionError: 'BiometricAndTextExtractionError',
  Liveness: 'Liveness',
  LivenessError: 'LivenessError',
  UserBiometricError: 'UserBiometricError',
  ...SecurityVerificationFileTypeEnum
};
const log = new Logger('VerificationModalComponent');
const LIVENESS_IFRAME_TIMEOUT_MS = 2 * 60 * 1000;

@Component({
  selector: 'app-verification-modal',
  templateUrl: './verification-modal.component.html',
  styleUrls: ['./verification-modal.component.scss'],
  standalone: false
})
export class VerificationModalComponent implements ModalPublicProperties, OnInit, AfterContentInit, OnDestroy {
  @Input() verification: SecurityVerificationType;
  @Input() documentId: string;
  @Input() operationId: string;
  @ViewChild('canvas', { static: false }) canvas: ElementRef<HTMLCanvasElement>;

  videoStream: MediaStream | null = null;
  step: string;
  picturesUrls: { [key: string]: string } = {};
  picturesImages: { [key: string]: File } = {};
  isLoading = false;
  verificationInProgress = false;
  firstLoad = true;
  currentUrl: string;
  referenceCode: SecurityVerificationPayloadType;
  currentFacingMode = 'environment';
  isNativeMobileCam = false;
  panelUrl: string;
  altPanelUrl: string;
  liveness: { url: SafeUrl | null; ready: boolean; error: string } = { url: null, ready: false, error: '' };
  signedUrls: BiometriqueSignedUrls;
  userBiometricValidation: UserBiometricValidation;

  readonly Steps = Steps;
  readonly SecurityVerificationEnum = SecurityVerificationEnum;
  readonly photoSteps: string[] = [Steps.Front, Steps.Back, Steps.Selfie];
  readonly errorSteps: string[] = [Steps.CameraError, Steps.VerificationError];
  private livenessIframeTimeout$ = new Subject<void>();

  private referenceSubscription: Subscription;
  private previousSteps: string[] = [];
  private destroy$ = new Subject<void>();

  get currentStep() {
    return this.step;
  }
  set currentStep(step: string) {
    this.previousSteps.unshift(this.step || Steps.Intro);
    this.step = step || Steps.Intro;
  }

  get introDescription(): string {
    if (!this.documentId) {
      return 'documents.documentVerificationPhotoAndFaceRequiredCreator';
    }

    return this.verification?.type === SecurityVerificationEnum.PfFacial ? 'documents.documentVerificationFacePhotoRequired' : 'documents.documentVerificationPhotoAndFaceRequired';
  }

  get stepInfo(): { title: string; description: string } {
    if (this.photoSteps.includes(this.currentStep) && this.picturesUrls[this.currentStep]) {
      return { title: 'documents.photoGood', description: 'documents.checkVisibility' };
    }
    switch (this.currentStep) {
      case Steps.Intro:
        return { title: 'documents.documentVerification', description: this.introDescription };
      case Steps.HasBackPrompt:
        return { title: 'documents.hasBackPhoto', description: 'documents.selectBackPhotoOption' };
      case Steps.Front:
        return { title: 'documents.takePhotoOfDocument', description: 'documents.alignDocumentInImage' };
      case Steps.Back:
        return { title: 'documents.takePhotoOfDocumentBack', description: 'documents.takePhotoOfDocumentBackDescription' };
      case Steps.Selfie:
        return { title: 'documents.takePhotoOfFace', description: 'documents.alignFaceInTheImage' };
      case Steps.MobilePrompt:
        return { title: 'documents.smartphoneVerification', description: 'documents.scanQRCode' };
      case Steps.CameraError:
        return { title: 'documents.biometricVerification', description: 'documents.cameraAccessError' };
      case Steps.VerificationError:
        return {
          title: 'documents.verificationFailure',
          description: this.verification?.type === SecurityVerificationEnum.Manual ? 'documents.verificationFailureMessage' : 'documents.lowSimilarityErrorMessage'
        };
      case Steps.BiometricAndTextExtractionError:
        return { title: 'documents.biometricAndTextExtractionError', description: 'documents.biometricAndTextExtractionErrorDesc' };
      case Steps.LivenessError:
        return { title: 'documents.livenessError', description: '' };
      case Steps.UserBiometricError:
        return { title: 'documents.userBiometricError', description: 'documents.userBiometricErrorDesc' };
      default:
        return { title: '', description: '' };
    }
  }

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    private ngZone: NgZone,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private loaderService: LoaderService,
    private appService: AppService,
    private whitelabelService: WhitelabelService,
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
    private authenticationService: AuthenticationService,
    private verificationService: VerificationService
  ) {
    this.panelUrl = environment.panelUrl;
    this.altPanelUrl = environment.production ? 'https://valida.ae' : environment.panelUrl + '/verificar';
  }

  ngOnInit() {
    if ([SecurityVerificationEnum.BiometricAndTextExtraction, SecurityVerificationEnum.LivenessAndTextExtraction].includes(this.verification.type)) {
      this.handleBiometriqueOperation();
    }

    if (this.verification.type === SecurityVerificationEnum.PfFacialMatch) {
      this.preparePfFacialMatchUrl().pipe(untilDestroyed(this)).subscribe();
    }
  }

  ngAfterContentInit() {
    this.resetPictures();
    this.currentStep = null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCamera();
    if (this.referenceSubscription) {
      this.referenceSubscription.unsubscribe();
    }
  }

  setInitialStep() {
    switch (this.verification.type) {
      case SecurityVerificationEnum.PfFacial:
      case SecurityVerificationEnum.PfFacialMatch:
        this.currentStep = Steps.Selfie;
        break;
      default:
        this.currentStep = Steps.Front;
        break;
    }

    this.currentFacingMode = this.isMobile() && this.currentStep === Steps.Selfie ? 'user' : 'environment';
  }

  goBack() {
    if (this.currentStep !== Steps.Intro) {
      // If the video is not active and the current step is already different from Intro,
      // reset to Intro and restart the process
      if (
        ((!this.videoStream || !this.videoStream.active) && !this.isNativeMobileCam) ||
        (this.verification.type === SecurityVerificationEnum.LivenessAndTextExtraction && [Steps.Front, Steps.LivenessError].includes(this.currentStep))
      ) {
        this.stopCamera();
        this.resetPictures();
        return;
      }

      this.currentStep = this.previousSteps[0] || Steps.Intro;
      this.previousSteps.splice(0, 2);
    } else {
      this.modal.dismiss();
    }
  }

  confirmPhoto() {
    if (this.currentStep === Steps.Front) {
      this.currentStep = this.isNativeMobileCam ? Steps.Back : Steps.HasBackPrompt;
    } else if (this.currentStep === Steps.Back) {
      if (this.verification.type === SecurityVerificationEnum.LivenessAndTextExtraction) {
        this.verify(this.picturesImages);
      } else {
        this.goToSelfie();
      }
    } else {
      this.verify(this.picturesImages);
    }
  }

  startDesktopCamera() {
    if (this.verification.type === SecurityVerificationEnum.LivenessAndTextExtraction) {
      this.handleLivenessSession();
    } else {
      this.loadCamera();
    }
  }

  startMobileCamera() {
    if (this.verification.type === SecurityVerificationEnum.LivenessAndTextExtraction) {
      this.handleLivenessSession();
    } else {
      this.startPhotoCapture();
    }
  }

  retryCamera() {
    if (this.verification.type === SecurityVerificationEnum.PfFacialMatch) {
      this.resetPfFacialMatch();
    } else if (this.verification.type === SecurityVerificationEnum.LivenessAndTextExtraction) {
      this.currentStep = Steps.Intro;
      this.handleBiometriqueOperation();
    } else {
      this.loadCamera();
    }
  }

  stopCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
    }
  }

  loadCamera(facingMode?: string) {
    this.stopCamera();
    if (!facingMode) this.setInitialStep();
    this.isLoading = true;
    this.loaderService.show();

    const canvasElement = this.canvas.nativeElement;
    const ctx = canvasElement.getContext('2d');
    const mode = facingMode || this.currentFacingMode;

    const constraints: MediaStreamConstraints = {
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        facingMode: mode
      }
    };

    timer(500)
      .pipe(
        switchMap(() => (this.canvas && this.canvas.nativeElement ? of(null) : throwError(() => ''))),
        switchMap(() => (navigator.mediaDevices && navigator.mediaDevices.getUserMedia ? from(navigator.mediaDevices.getUserMedia(constraints)) : throwError(() => ''))),
        tap((stream: MediaStream) => {
          this.videoStream = stream;
          const video = document.createElement('video');

          video.srcObject = stream;
          video.muted = true;
          video.playsInline = true;
          video.autoplay = true;

          video.play();

          const renderFrame = () => {
            if (video.readyState === video.HAVE_ENOUGH_DATA) {
              ctx.save();

              if (this.isMobile() && mode === 'user') {
                ctx.scale(-1, 1);
                ctx.translate(-canvasElement.width, 0);
              }

              ctx.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);
              ctx.restore();
            }
            requestAnimationFrame(renderFrame);
          };

          video.addEventListener('loadedmetadata', () => {
            canvasElement.width = video.videoWidth;
            canvasElement.height = video.videoHeight;
            renderFrame();
          });

          video.addEventListener('resize', () => {
            this.handleOrientationChange(video);
          });
        }),
        catchError(error => {
          if (error.name === 'NotAllowedError') {
            this.videoStream = null;
            this.currentStep = Steps.CameraError;
            return of(null);
          }
          return throwError(() => error);
        }),
        finalize(() => {
          this.firstLoad = false;
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        error: error => {
          this.videoStream = null;
          this.currentStep = Steps.CameraError;
          log.error(error);
        }
      });
  }

  takePicture() {
    if (!this.videoStream || !this.videoStream.active) {
      this.currentStep = Steps.CameraError;
      return;
    }

    if (this.isLoading) return;
    this.isLoading = true;
    const currentStep = this.currentStep;

    const canvas = this.captureCanvas(this.canvas.nativeElement);
    canvas.toBlob(blob => {
      this.ngZone.run(() => {
        const isSerpro = [SecurityVerificationEnum.PfFacial, SecurityVerificationEnum.PfFacialMatch].includes(this.verification.type);
        const extension = isSerpro ? 'png' : 'jpeg';
        const mime = isSerpro ? 'image/png' : 'image/jpeg';
        const file = new File([blob], `${currentStep}.${extension}`, { type: mime, lastModified: Date.now() });
        this.savePicture(currentStep, file);
        this.isLoading = false;
      });
    }, 'image/jpeg');
  }

  captureCanvas(sourceCanvas: HTMLCanvasElement, targetLongSide: number = 1600, pixelRatio: number = 1): HTMLCanvasElement {
    const displayWidth = sourceCanvas.offsetWidth;
    const displayHeight = sourceCanvas.offsetHeight;
    const realWidth = sourceCanvas.width;
    const realHeight = sourceCanvas.height;
    const displayRatio = displayWidth / displayHeight;
    const realRatio = realWidth / realHeight;

    // área de crop na imagem real, aplica o mesmo crop do object-fit: cover
    let sx = 0;
    let sy = 0;
    let sWidth = realWidth;
    let sHeight = realHeight;
    if (realRatio > displayRatio) {
      // imagem mais larga, corta laterais
      sWidth = realHeight * displayRatio;
      sx = (realWidth - sWidth) / 2;
    } else {
      // imagem mais alta, corta topo/base
      sHeight = realWidth / displayRatio;
      sy = (realHeight - sHeight) / 2;
    }

    let targetWidth: number;
    let targetHeight: number;
    const maxSourceSide = Math.max(sWidth, sHeight);
    const safeTargetLongSide = Math.min(targetLongSide, maxSourceSide);
    if (displayWidth > displayHeight) {
      // landscape
      targetWidth = safeTargetLongSide;
      targetHeight = safeTargetLongSide / displayRatio;
    } else {
      // portrait
      targetHeight = safeTargetLongSide;
      targetWidth = safeTargetLongSide * displayRatio;
    }

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = Math.round(targetWidth * pixelRatio);
    outputCanvas.height = Math.round(targetHeight * pixelRatio);

    const ctx = outputCanvas.getContext('2d');
    ctx.scale(pixelRatio, pixelRatio);
    ctx.drawImage(
      sourceCanvas,
      sx, // origem do crop
      sy,
      sWidth, // tamanho do crop
      sHeight,
      0, // destino
      0,
      targetWidth, // tamanho final
      targetHeight
    );

    return outputCanvas;
  }

  takePictureMobile(image: File) {
    if (!image) return;
    this.isLoading = true;
    this.savePicture(this.currentStep, image);
    this.isLoading = false;
  }

  useFrontAsBackPicture() {
    if (this.picturesUrls[Steps.Back]) URL.revokeObjectURL(this.picturesUrls[Steps.Back]);
    delete this.picturesUrls[Steps.Back];
    delete this.picturesImages[Steps.Back];
    if (this.verification.type === SecurityVerificationEnum.LivenessAndTextExtraction) {
      this.verify(this.picturesImages);
    } else {
      this.goToSelfie();
    }
  }

  verify(images: { [key: string]: File }) {
    this.isLoading = true;
    this.verificationInProgress = true;
    this.stopCamera();

    const imageKeys = Object.keys(images);
    const urlKeyMap: Partial<Record<SecurityVerificationFileTypeEnum, keyof BiometriqueSignedUrls>> = {
      [SecurityVerificationFileTypeEnum.Front]: 'front_side',
      [SecurityVerificationFileTypeEnum.Back]: 'back_side',
      [SecurityVerificationFileTypeEnum.Selfie]: 'selfie'
    };

    if ([SecurityVerificationEnum.BiometricAndTextExtraction, SecurityVerificationEnum.LivenessAndTextExtraction, SecurityVerificationEnum.PfFacialMatch].includes(this.verification.type)) {
      concat(
        ...imageKeys.map(key => {
          const urlKey = urlKeyMap[key as keyof typeof urlKeyMap];
          const signedUrl = this.signedUrls?.[urlKey];
          if (!signedUrl) {
            return of(null);
          }
          return this.uploadFileToSignedUrl(images[key], signedUrl);
        })
      )
        .pipe(
          toArray(),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: () => {
            if (this.verification.type === SecurityVerificationEnum.PfFacialMatch) {
              this.verifyBiometricAuth();
            } else {
              this.handleExecuteBiometriqueOperation();
            }
          },
          error: error => {
            this.verificationInProgress = false;
            this.currentStep = Steps.VerificationError;
            this.stopCamera();
            this.resetPictures();
            this.errorHandlerService.handle(error);
          }
        });
    } else {
      of(imageKeys.map(key => this.verificationService.updateSecurityVerification({ id: this.verification.id, fileType: key as SecurityVerificationFileTypeEnum, file: images[key] })))
        .pipe(
          switchMap(requests => concat(...requests).pipe(toArray())),
          map(verifications => verifications[verifications.length - 1]),
          finalize(() => {
            this.isLoading = false;
            this.verificationInProgress = false;
          })
        )
        .subscribe({
          next: data => {
            this.verification = data;
            if (data?.verified_at || data?.type === SecurityVerificationEnum.Manual) {
              this.modal.close(data as ModalResult);
            } else {
              this.notyService.error((data.logs_attempt && data.logs_attempt[0][0]?.message) || this.translateService.instant('error_pf_facial_mismatch'));
              this.currentStep = Steps.VerificationError;
              this.resetPictures();
              if (data?.max_attempt === 0) {
                this.modal.close(data as ModalResult);
              }
            }
          },
          error: error => {
            this.currentStep = Steps.VerificationError;
            this.resetPictures();
            this.errorHandlerService.handle(error);
          }
        });
    }
  }

  selectDesktopMobilePrompt() {
    this.currentStep = Steps.MobilePrompt;

    // Se já tiver uma operação de validação biométrica,
    // direciona para a URL de verificação com o parâmetro de userValidation para exibir o QR code diretamente,
    // caso contrário, inicia o processo normalmente
    if (this.userBiometricValidation?.operation_id) {
      this.currentUrl = `${this.panelUrl}/verificar/${this.userBiometricValidation.operation_id}?userValidation=1`;
      this.referenceCode = {
        reference: this.userBiometricValidation.operation_id,
        expiry: new Date(Date.now() + 10 * 60 * 1000).toISOString()
      };
      return;
    }

    this.isLoading = true;
    this.loadWhitelabelAndUpdateUrls$()
      .pipe(
        switchMap(() => this.generateSecurityVerificationCode()),
        switchMap(() => (this.currentUrl ? of(null) : this.verificationService.signatureBySecurityVerificationCode({ code: this.referenceCode.reference }))),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: signatureId => {
          if (!this.currentUrl) {
            this.currentUrl = [this.panelUrl, 'assinar', signatureId].join('/') + '?signPrompt=1';
            this.referenceSubscription = interval(5000)
              .pipe(
                filter(() => this.referenceCode && new Date(this.referenceCode.expiry) < new Date()),
                switchMap(() => this.generateSecurityVerificationCode())
              )
              .subscribe();
          }
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  toggleCamera() {
    const currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
    this.currentFacingMode = currentFacingMode;
    this.loadCamera(currentFacingMode);
  }

  isMobile() {
    return ['mobile'].includes(Bowser.getParser(window.navigator.userAgent).getPlatformType());
  }

  tryNativeMobileCam() {
    this.isNativeMobileCam = true;
    this.resetPictures();
    this.stopCamera();
    this.setInitialStep();
  }

  resetBiometriqueOperation() {
    this.resetPictures();
    this.handleBiometriqueOperation();
  }

  createBiometriqueOperation() {
    this.isLoading = true;
    this.verificationService
      .createBiometriqueOperation({ id: this.verification.id })
      .pipe(
        tap(verification => {
          this.verification = verification;
        }),
        switchMap(() => this.verificationService.biometriqueOperationUrls({ id: this.verification.id })),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: urls => {
          this.setSignedUrls(urls);
        },
        error: error => {
          if (error.message === 'operation_already_running_for_this_verification') {
            this.verificationInProgress = true;
            this.checkSecurityVerificationPolling();
          } else if (error.message === 'security_verification_already_verified') {
            this.securityVerification();
          } else {
            this.currentStep = Steps.CameraError;
            this.errorHandlerService.handle(error);
          }
        }
      });
  }

  executeBiometriqueVerification() {
    this.isLoading = true;
    this.verificationService
      .executeBiometriqueVerification({ verification_id: this.verification.id })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.checkSecurityVerificationPolling();
        },
        error: error => {
          this.verificationInProgress = false;
          this.resetPictures();
          this.errorHandlerService.handle(error);
        }
      });
  }

  verifyBiometricAuth() {
    this.loaderService.disableHttpLoader = true;
    this.authenticationService
      .verifyBiometricAuth({ verification_id: this.verification.id, document_id: this.documentId })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: ({ authentication, verification }) => {
          if (verification) {
            this.verification = verification;
          } else if (authentication) {
            this.verification.verified_at = new Date().toDateString();
          }
        },
        complete: () => {
          this.loaderService.disableHttpLoader = false;
          this.verificationInProgress = false;
          this.checkVerifiedAt(this.verification);
        },
        error: error => {
          this.resetPictures();
          this.errorHandlerService.handle(error);
        }
      });
  }

  checkSecurityVerificationPolling() {
    this.loaderService.disableHttpLoader = true;
    return timer(5000, 5000)
      .pipe(
        switchMap(() => this.verificationService.checkSecurityVerification({ document_id: this.documentId, id: this.verification.id })),
        takeWhile(data => !this.isVerified(data), true),
        takeUntil(timer(120000)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: data => {
          this.verification = data;
        },
        complete: () => {
          this.loaderService.disableHttpLoader = false;
          this.verificationInProgress = false;
          this.syncPicturesUrls(this.verification?.user?.images);
          this.checkVerifiedAt(this.verification);
        },
        error: error => {
          this.resetPictures();
          this.errorHandlerService.handle(error);
        }
      });
  }

  isVerified(data: SecurityVerificationType): boolean {
    return !!(data?.verified_at || data.payload?.failcode || data?.type === SecurityVerificationEnum.Manual);
  }

  checkVerifiedAt(data: SecurityVerificationType) {
    if (data.max_attempt <= 0) {
      this.modal.close(data as ModalResult);
    } else if (data?.type === SecurityVerificationEnum.Manual) {
      this.notyService.warning(this.translateService.instant('notyService.biometricRejectedManualVerification'));
      this.modal.close(data as ModalResult);
    } else if (data?.verified_at) {
      this.modal.close(data as ModalResult);
    } else if (data?.type === SecurityVerificationEnum.PfFacialMatch) {
      this.resetPictures();
      this.currentStep = Steps.VerificationError;
    } else {
      this.currentStep = Steps.BiometricAndTextExtractionError;
    }
  }

  securityVerification() {
    this.isLoading = true;
    this.loaderService.show();
    this.verificationService
      .securityVerification({ document_id: this.documentId, verification_id: this.verification.id })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: data => {
          this.checkVerifiedAt(data);
        },
        error: error => {
          this.errorHandlerService.handle(error);
        }
      });
  }

  resetPicture() {
    this.picturesUrls[this.currentStep] = null;
    this.picturesImages[this.currentStep] = null;
  }

  createLivenessSession() {
    this.isLoading = true;
    this.verificationInProgress = true;
    this.loadWhitelabelAndUpdateUrls$()
      .pipe(
        switchMap(() => this.verificationService.createLivenessSession({ verification_id: this.verification.id })),
        finalize(() => {
          this.isLoading = false;
          this.verificationInProgress = false;
        })
      )
      .subscribe({
        next: data => {
          const url = `${environment.biometricPanelUrl}?sessionId=${data}&language=${this.translateService.currentLang}&mobile=${this.isMobile()}`;
          this.openLivenessIframe(url);
        },
        error: error => {
          this.currentStep = Steps.CameraError;
          this.errorHandlerService.handle(error);
        }
      });
  }

  triggerLivenessButton() {
    const iframe: HTMLIFrameElement | null = document.querySelector('#liveness-iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({ type: 'START_LIVENESS' }, '*');
    }
  }

  checkLivenessResults() {
    this.verificationService.checkLivenessResults({ verification_id: this.verification.id }).subscribe({
      next: data => {
        if (data.status === LivenessStatusEnum.Succeeded) {
          this.startPhotoCapture();
        } else {
          this.setLivenessError();
        }
      },
      error: error => {
        if (['missing_operation_id', 'missing_session_id', 'reference_image_not_found'].includes(error.message)) {
          this.setLivenessError();
          return;
        }
        this.errorHandlerService.handle(error);
      }
    });
  }

  setLivenessError() {
    this.currentStep = Steps.LivenessError;
    this.liveness.error = 'status_failed';
  }

  resetPfFacialMatch() {
    this.preparePfFacialMatchUrl()
      .pipe(tap(() => this.loadCamera()))
      .subscribe();
  }

  @HostListener('window:message', ['$event'])
  onIframeMessage(event: MessageEvent) {
    switch (event.data.type) {
      case 'SHOW_LIVENESS_BUTTON':
        this.liveness.ready = true;
        break;
      case 'HIDDEN_LIVENESS_BUTTON':
        this.liveness.ready = false;
        break;
      case 'LIVENESS_COMPLETE':
        this.liveness.ready = false;
        this.handleCheckLivenessResults();
        break;
      case 'LIVENESS_ERROR':
        this.liveness.ready = false;
        this.currentStep = Steps.LivenessError;
        this.liveness.error = event.data?.livenessError?.state;
        break;
    }
  }

  private goToSelfie() {
    if (!this.isNativeMobileCam && this.isMobile() && this.currentFacingMode === 'environment') {
      this.toggleCamera();
    }
    this.currentStep = Steps.Selfie;
  }

  private startPhotoCapture() {
    if (this.isNativeMobileCam) {
      this.tryNativeMobileCam();
    } else {
      this.loadCamera();
    }
  }

  private loadWhitelabelAndUpdateUrls$(): Observable<any> {
    return (
      this.documentId && this.appService.isAutentiqueDomain()
        ? this.whitelabelService.loadWhitelabelByDocument({ documentId: this.documentId }, { fetchPolicy: 'cache-first' })
        : this.whitelabelService.loadWhitelabelByDomain({ fetchPolicy: 'cache-first' })
    ).pipe(
      tap(whitelabel => {
        this.panelUrl = whitelabel.domain ? `https://${whitelabel.domain}` : this.panelUrl;
        this.altPanelUrl = whitelabel.domain ? `https://${whitelabel.domain}/verificar` : this.altPanelUrl;
      })
    );
  }

  private savePicture(step: string, file: File) {
    if (this.picturesUrls[step]) URL.revokeObjectURL(this.picturesUrls[step]);
    this.picturesImages[step] = file;
    this.picturesUrls[step] = URL.createObjectURL(file);
  }

  private preparePfFacialMatchUrl(): Observable<any> {
    return this.verificationService.prepareBiometricAuth({ document_id: this.documentId, security_verification_id: this.verification.id }).pipe(
      tap(selfieUrl => {
        this.signedUrls = { selfie: selfieUrl };
      }),
      catchError(error => {
        this.errorHandlerService.handle(error);
        return throwError(() => error);
      })
    );
  }

  private handleOrientationChange(video: HTMLVideoElement) {
    const canvas = this.canvas.nativeElement;
    const width = video.videoWidth;
    const height = video.videoHeight;

    if (!width || !height) return;

    canvas.width = width;
    canvas.height = height;
  }

  private syncPicturesUrls(images: VerificationImages) {
    if (images?.front && !this.picturesUrls[Steps.Front]) this.picturesUrls[Steps.Front] = images.front;
    if (images?.back && !this.picturesUrls[Steps.Back]) this.picturesUrls[Steps.Back] = images.back;
    if (images?.selfie && !this.picturesUrls[Steps.Selfie]) this.picturesUrls[Steps.Selfie] = images.selfie;
  }

  private generateSecurityVerificationCode() {
    this.isLoading = true;
    if (this.verification.type === SecurityVerificationEnum.PfFacialMatch && !this.authenticationService.isAuthenticated()) {
      const signatureLinkId = window.location.pathname.split('/assinar/')[1]?.split('/')[0];
      this.currentUrl = `${window.location.origin}/assinar/${signatureLinkId}?signPrompt=1`;
      this.referenceCode = {
        reference: signatureLinkId,
        expiry: new Date(Date.now() + 10 * 60 * 1000).toISOString()
      };
      this.isLoading = false;
      return of(this.referenceCode);
    }
    return this.verificationService.generateSecurityVerificationCode({ id: this.verification.id }).pipe(tap(data => (this.referenceCode = data)));
  }

  private resetPictures() {
    this.currentStep = null;
    Object.values(this.picturesUrls || {}).forEach(pic => pic && URL.revokeObjectURL(pic));
    this.picturesUrls = {};
    this.picturesImages = {};
  }

  private uploadFileToSignedUrl(file: File, signedUrl: string): Observable<any> {
    const maxMB = 4;
    const maxBYTES = maxMB * 1024 * 1024;

    const prepareFile$ = file.size > maxBYTES ? this.appService.downsizeImage(file, 2000) : of(file);
    return prepareFile$.pipe(
      switchMap((finalFile: File) => {
        return this.httpClient.put(signedUrl, finalFile, {
          headers: {
            'Content-Type': finalFile.type
          }
        });
      })
    );
  }

  private handleBiometriqueOperation() {
    if (this.documentId) {
      this.createBiometriqueOperation();
    } else if (this.operationId) {
      this.fetchAndSetUserBiometricValidation(this.operationId);
    } else {
      this.startUserBiometricVerification();
    }
  }

  private handleLivenessSession() {
    if (this.documentId) {
      this.createLivenessSession();
    } else {
      this.createUserBiometricLivenessSession();
    }
  }

  private handleCheckLivenessResults() {
    if (this.documentId) {
      this.checkLivenessResults();
    } else {
      this.checkUserBiometricLivenessResults();
    }
  }

  private handleExecuteBiometriqueOperation() {
    if (this.documentId) {
      this.executeBiometriqueVerification();
    } else {
      this.executeUserBiometricVerification();
    }
  }

  private startUserBiometricVerification$(): Observable<any> {
    return this.verificationService.startUserBiometricValidation().pipe(
      tap(userBiometricValidation => {
        this.userBiometricValidation = userBiometricValidation;
      }),
      switchMap(() => {
        if (this.userBiometricValidation.status === UserBiometricValidationStatusEnum.Processing) {
          this.verificationInProgress = true;
          this.pollingUserBiometricValidation();
          return EMPTY;
        }
        return this.biometricSignedUrls(this.userBiometricValidation.operation_id);
      })
    );
  }

  private startUserBiometricVerification() {
    this.isLoading = true;
    this.startUserBiometricVerification$()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        error: error => {
          if (error.message === 'user_biometric_waiting_approval') {
            this.currentStep = Steps.UserBiometricError;
          } else if (error.message === 'user_biometric_already_verified') {
            const now = new Date().toISOString();
            const userBiometricValidation: UserBiometricValidation = {
              created_at: this.userBiometricValidation?.created_at || now,
              id: this.userBiometricValidation?.id || 'already-verified',
              max_attempts: this.userBiometricValidation?.max_attempts || 0,
              operation_id: this.userBiometricValidation?.operation_id || this.operationId || 'already-verified',
              status: UserBiometricValidationStatusEnum.Completed,
              tries: this.userBiometricValidation?.tries || 3,
              updated_at: this.userBiometricValidation?.updated_at || now
            };
            this.modal.close(userBiometricValidation);
          } else {
            this.currentStep = Steps.CameraError;
            this.errorHandlerService.handle(error);
          }
        }
      });
  }

  private createUserBiometricLivenessSession() {
    this.isLoading = true;
    this.verificationInProgress = true;

    const ensureOperation$ = this.userBiometricValidation?.operation_id ? of(this.userBiometricValidation) : this.startUserBiometricVerification$();

    ensureOperation$
      .pipe(
        switchMap(() => this.verificationService.createUserBiometricLivenessSession({ operation_id: this.userBiometricValidation.operation_id })),
        finalize(() => {
          this.isLoading = false;
          this.verificationInProgress = false;
        })
      )
      .subscribe({
        next: data => {
          const url = `${environment.biometricPanelUrl}?sessionId=${data}&language=${this.translateService.currentLang}&mobile=${this.isMobile()}`;
          this.openLivenessIframe(url);
        },
        error: error => {
          this.currentStep = Steps.CameraError;
          this.errorHandlerService.handle(error);
        }
      });
  }

  private checkUserBiometricLivenessResults() {
    this.verificationService.checkUserBiometricLivenessResults({ operation_id: this.userBiometricValidation.operation_id }).subscribe({
      next: data => {
        if (data.status === LivenessStatusEnum.Succeeded) {
          this.startPhotoCapture();
        } else {
          this.setLivenessError();
        }
      },
      error: error => {
        if (['missing_operation_id', 'missing_session_id', 'reference_image_not_found'].includes(error.message)) {
          this.setLivenessError();
          return;
        }
        this.errorHandlerService.handle(error);
      }
    });
  }

  private openLivenessIframe(url: string) {
    // Cancela qualquer timer antigo que ainda esteja pendente
    this.livenessIframeTimeout$.next();

    this.liveness.url = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.currentStep = Steps.Liveness;

    timer(LIVENESS_IFRAME_TIMEOUT_MS)
      .pipe(takeUntil(this.livenessIframeTimeout$), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.currentStep !== Steps.Liveness) return;
        this.liveness.error = 'TIMEOUT';
        this.currentStep = Steps.LivenessError;
      });
  }

  private executeUserBiometricVerification() {
    this.isLoading = true;
    this.verificationService
      .executeUserBiometricValidation({ operation_id: this.userBiometricValidation.operation_id })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.pollingUserBiometricValidation();
        },
        error: error => {
          this.verificationInProgress = false;
          this.resetPictures();
          this.errorHandlerService.handle(error);
        }
      });
  }

  private pollingUserBiometricValidation() {
    this.loaderService.disableHttpLoader = true;
    return timer(5000, 5000)
      .pipe(
        switchMap(() => this.verificationService.userBiometricValidation({ operation_id: this.userBiometricValidation.operation_id })),
        takeWhile(data => !(data?.verified_at || data.status !== UserBiometricValidationStatusEnum.Processing), true),
        takeUntil(timer(120000)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: data => {
          this.userBiometricValidation = data;
        },
        complete: () => {
          if ([UserBiometricValidationStatusEnum.Completed, UserBiometricValidationStatusEnum.Approved].includes(this.userBiometricValidation.status) && this.userBiometricValidation.verified_at) {
            this.modal.close(this.userBiometricValidation as ModalResult);
          } else if (this.userBiometricValidation.status === UserBiometricValidationStatusEnum.WaitingApproval && !this.userBiometricValidation.verified_at) {
            this.currentStep = Steps.UserBiometricError;
          } else {
            this.currentStep = Steps.BiometricAndTextExtractionError;
          }
          this.syncPicturesUrls(this.userBiometricValidation?.payload?.image);
          this.loaderService.disableHttpLoader = false;
          this.verificationInProgress = false;
        },
        error: error => {
          this.resetPictures();
          this.errorHandlerService.handle(error);
        }
      });
  }

  private fetchAndSetUserBiometricValidation(operation_id: string) {
    this.isLoading = true;
    this.verificationService
      .userBiometricValidation({ operation_id })
      .pipe(
        tap(userBiometricValidation => {
          this.userBiometricValidation = userBiometricValidation;
        }),
        switchMap(() => this.biometricSignedUrls(this.userBiometricValidation.operation_id)),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        error: error => {
          this.errorHandlerService.handle(error);
        }
      });
  }

  private biometricSignedUrls(operationId: string) {
    return this.verificationService.biometricSignedUrls({ operation_id: operationId }).pipe(tap(urls => this.setSignedUrls(urls)));
  }

  private setSignedUrls(urls: BiometriqueSignedUrls) {
    this.signedUrls = {
      front_side: urls.front_side,
      back_side: urls.back_side,
      selfie: urls.selfie
    };
  }
}
